// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { DecisionTreeResponse, IncrementalResults, Metrics } from '../../../services/decisionTreeService';

interface DecisionTreeResultsProps {
  results: DecisionTreeResponse;
  computedAt: number;
  onClose: () => void;
}

export const DecisionTreeResults: React.FC<DecisionTreeResultsProps> = ({
  results,
  computedAt,
  onClose,
}) => {
  // Convert raw types to domain types (handle arrays as data quality issues)
  const toNumber = (val: number | number[] | undefined | null, fieldName: string): number => {
    if (val == null) return NaN;
    if (typeof val === 'number') return val;
    if (Array.isArray(val)) {
      console.warn(`[DecisionTreeResults] Array encountered for ${fieldName}, using first element:`, val);
      return val.length > 0 ? (typeof val[0] === 'number' ? val[0] : NaN) : NaN;
    }
    return NaN;
  };

  const incremental: IncrementalResults = {
    delta_qalys: toNumber(results.incremental.delta_qalys, 'incremental.delta_qalys'),
    delta_life_years: toNumber(results.incremental.delta_life_years, 'incremental.delta_life_years'),
    delta_cost: toNumber(results.incremental.delta_cost, 'incremental.delta_cost'),
    deaths_averted: toNumber(results.incremental.deaths_averted, 'incremental.deaths_averted'),
    cases_avoided: toNumber(results.incremental.cases_avoided, 'incremental.cases_avoided'),
    hospitalizations_avoided: toNumber(results.incremental.hospitalizations_avoided, 'incremental.hospitalizations_avoided'),
    events_prevented: toNumber(results.incremental.events_prevented, 'incremental.events_prevented'),
  };

  const metrics: Metrics = {
    icer: toNumber(results.metrics.icer, 'metrics.icer'),
    bcr: toNumber(results.metrics.bcr, 'metrics.bcr'),
    budget_impact: toNumber(results.metrics.budget_impact, 'metrics.budget_impact'),
  };

  const formatNumber = (n: number | undefined | null) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Math.round(n).toLocaleString('en-US');
  };
  const formatCurrency = (n: number | undefined | null, roundedDecimals = 0) => {
    if (n == null || isNaN(n)) return 'N/A';
    if (roundedDecimals > 0) {
      if (typeof n !== 'number') {
        n = Number(n);
      }
      return `€${n.toLocaleString('en-US', { minimumFractionDigits: roundedDecimals, maximumFractionDigits: roundedDecimals })}`;
    }
    return `€${n.toLocaleString('en-US')}`;
  };
  const formatDecimal = (n: number | undefined | null, decimals: number = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    const num = typeof n === 'number' ? n : Number(n);
    if (isNaN(num)) return 'N/A';
    return num.toFixed(decimals);
  };
  
  const timeAgo = () => {
    const seconds = Math.floor((Date.now() - computedAt) / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };

  return (
    <div
      className="decision-tree-results-modal"
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 1000,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#f1f5f9', margin: 0 }}>
            Computation Results
          </h2>
          <p style={{ color: '#94a3b8', margin: '4px 0 0 0' }}>
            Computed {timeAgo()} • {formatDecimal(results.elapsed_ms / 1000, 1)}s elapsed
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: '1px solid #475569',
            borderRadius: '4px',
            color: '#f1f5f9',
            cursor: 'pointer',
            padding: '6px 12px',
          }}
        >
          Close
        </button>
      </div>

      {/* Key Metrics */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#f1f5f9', marginBottom: '12px' }}>
          Economic Metrics
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <MetricCard
            label="ICER"
            value={formatCurrency(metrics.icer, 1)}
            subtitle="per QALY gained"
            color="#3b82f6"
          />
          <MetricCard
            label="BCR"
            value={formatDecimal(metrics.bcr, 1)}
            subtitle="Benefit-Cost Ratio"
            color="#10b981"
          />
          <MetricCard
            label="Annual Budget Impact"
            value={formatCurrency(metrics.budget_impact)}
            subtitle={metrics.budget_impact < 0 ? 'Savings' : 'Additional Cost'}
            color={metrics.budget_impact < 0 ? '#10b981' : '#ef4444'}
          />
        </div>
      </div>

      {/* Incremental Results */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#f1f5f9', marginBottom: '12px' }}>
          Incremental Outcomes (Δ = Vaccinate - No Vaccinate)
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <ResultRow label="Life-Years Gained" value={formatNumber(incremental.delta_life_years)} positive />
          <ResultRow label="QALYs Gained" value={formatDecimal(incremental.delta_qalys, 3)} positive />
          <ResultRow label="Deaths Averted" value={formatDecimal(-1*incremental.deaths_averted, 3)} positive />
          <ResultRow label="Cases Avoided" value={formatNumber(-1*incremental.cases_avoided)} positive />
          <ResultRow label="Hospitalizations Avoided" value={formatNumber(-1*incremental.hospitalizations_avoided)} positive />
          <ResultRow label="Cost Difference" value={formatCurrency(incremental.delta_cost)} />
        </div>
      </div>

      {/* Strategy Comparison */}
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ color: '#f1f5f9', marginBottom: '12px' }}>
          Strategy Comparison
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* No Vaccinate */}
          <div style={{ background: '#0f172a', padding: '16px', borderRadius: '6px', border: '1px solid #334155' }}>
            <h4 style={{ color: '#f1f5f9', marginBottom: '12px' }}>
              No Vaccinate
            </h4>
            <StrategyMetric label="Life-Years" value={formatNumber(results.results.no_vaccinate.life_years)} />
            <StrategyMetric label="QALYs" value={formatDecimal(results.results.no_vaccinate.qalys, 3)} />
            <StrategyMetric label="Deaths" value={formatNumber(results.results.no_vaccinate.deaths)} />
            <StrategyMetric label="Cases" value={formatNumber(results.results.no_vaccinate.cases)} />
            <StrategyMetric label="Hospitalizations" value={formatNumber(results.results.no_vaccinate.hospitalizations)} />
            <StrategyMetric label="Total Cost" value={formatCurrency(results.results.no_vaccinate.total_cost)} />
          </div>

          {/* Vaccinate */}
          <div style={{ background: '#0f172a', padding: '16px', borderRadius: '6px', border: '1px solid #334155' }}>
            <h4 style={{ color: '#f1f5f9', marginBottom: '12px' }}>
              Vaccinate
            </h4>
            <StrategyMetric label="Life-Years" value={formatNumber(results.results.vaccinate.life_years)} />
            <StrategyMetric label="QALYs" value={formatDecimal(results.results.vaccinate.qalys, 3)} />
            <StrategyMetric label="Deaths" value={formatNumber(results.results.vaccinate.deaths)} />
            <StrategyMetric label="Cases" value={formatNumber(results.results.vaccinate.cases)} />
            <StrategyMetric label="Hospitalizations" value={formatNumber(results.results.vaccinate.hospitalizations)} />
            <StrategyMetric label="Total Cost" value={formatCurrency(results.results.vaccinate.total_cost)} />
          </div>
        </div>
      </div>

      {/* Request ID for debugging */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #334155' }}>
        <p style={{ color: '#64748b', margin: 0 }}>
          Request ID: {results.request_id}
        </p>
      </div>
    </div>
  );
};

// Overlay backdrop
export const DecisionTreeResultsOverlay: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 999,
    }}
  />
);

// Helper components
const MetricCard: React.FC<{ label: string; value: string; subtitle: string; color: string }> = ({
  label,
  value,
  subtitle,
  color,
}) => (
  <div
    style={{
      background: '#0f172a',
      padding: '16px',
      borderRadius: '6px',
      border: `1px solid ${color}33`,
    }}
  >
    <div className="decision-tree-results-metric-label" style={{ color: '#94a3b8', marginBottom: '4px' }}>{label}</div>
    <div className="decision-tree-results-metric-value" style={{ color, marginBottom: '4px' }}>{value}</div>
    <div className="decision-tree-results-metric-subtitle" style={{ color: '#64748b' }}>{subtitle}</div>
  </div>
);

const ResultRow: React.FC<{ label: string; value: string; positive?: boolean }> = ({
  label,
  value,
  positive,
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 12px',
      background: '#0f172a',
      borderRadius: '4px',
      border: '1px solid #334155',
    }}
  >
    <span className="decision-tree-results-row-label" style={{ color: '#94a3b8' }}>{label}</span>
    <span className="decision-tree-results-row-value" style={{ color: positive ? '#10b981' : '#f1f5f9', fontWeight: '600' }}>
      {value}
    </span>
  </div>
);

const StrategyMetric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
    }}
  >
    <span className="decision-tree-results-strategy-metric" style={{ color: '#94a3b8' }}>{label}:</span>
    <span className="decision-tree-results-strategy-metric" style={{ color: '#f1f5f9', fontWeight: '500' }}>{value}</span>
  </div>
);
